import { useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { ReportReason } from "./types";
import { resolveEmergencyNumber } from "./types";

interface PressEmergencyArgs {
  geolocate?: boolean;
  /** 1 = silent contact+record+GPS. 2 = locale dial. Defaults to 1. */
  stage?: 1 | 2;
}

export function useSafety() {
  const pressEmergency = useCallback(async (args: PressEmergencyArgs = {}) => {
    const stage = args.stage ?? 1;
    const locale = typeof navigator !== "undefined" ? navigator.language : null;
    const dialed = resolveEmergencyNumber(locale);

    let lat: number | null = null;
    let lng: number | null = null;
    let accuracy: number | null = null;

    if (args.geolocate !== false && typeof navigator !== "undefined" && "geolocation" in navigator) {
      try {
        const pos = await new Promise<GeolocationPosition>((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: true,
            timeout: 4000,
            maximumAge: 30_000,
          });
        });
        lat = pos.coords.latitude;
        lng = pos.coords.longitude;
        accuracy = pos.coords.accuracy;
      } catch {
        // Best-effort — continue without coords
      }
    }

    const { data, error } = await (supabase as any).rpc("fn_press_emergency", {
      _lat: lat,
      _lng: lng,
      _accuracy_m: accuracy,
      _locale: locale,
      _dialed_number: dialed,
    });

    return { event_id: data as string | null, dialed, error: error?.message ?? null };
  }, []);

  /**
   * Block a member with optional receipt context (last DM excerpt + report id).
   * Calls fn_block_with_receipt — falls back to fn_block_user if the RPC is
   * unavailable (older deployments).
   */
  const blockUser = useCallback(
    async (
      targetId: string,
      receipt?: { lastMessageId?: string; lastMessageExcerpt?: string; reportId?: string },
    ) => {
      const { error } = await (supabase as any).rpc("fn_block_with_receipt", {
        _target: targetId,
        _last_message_id: receipt?.lastMessageId ?? null,
        _last_message_excerpt: receipt?.lastMessageExcerpt ?? null,
        _report_id: receipt?.reportId ?? null,
      });
      if (error) {
        // Fallback to legacy block path
        const { error: legacyErr } = await supabase.rpc("fn_block_user", { _target: targetId });
        return { error: legacyErr?.message ?? error.message };
      }
      return { error: null };
    },
    [],
  );

  const unblockUser = useCallback(async (targetId: string) => {
    const { error } = await supabase.rpc("fn_unblock_user", { _target: targetId });
    return { error: error?.message ?? null };
  }, []);

  const reportUser = useCallback(
    async (targetId: string, reason: ReportReason, context?: string, messageId?: string) => {
      const { data, error } = await supabase.rpc("fn_report_user", {
        _target: targetId,
        _reason: reason,
        _context: context ?? null,
        _message_id: messageId ?? null,
      });
      return { report_id: data as string | null, error: error?.message ?? null };
    },
    [],
  );

  return { pressEmergency, blockUser, unblockUser, reportUser };
}
